import { memo, useMemo } from 'react';
import type { HeroImageProps } from './types';
import './HeroImage.css';

/**
 * HeroImage Component
 *
 * A performant, accessible image component for hero sections and feature displays.
 * Includes lazy loading and proper accessibility attributes. Sizing and object-fit
 * are controlled via CSS (`HeroImage.css` / consumer stylesheets), not inline styles.
 *
 * @example
 * ```tsx
 * // Basic usage
 * <HeroImage
 *   src="https://example.com/image.jpg"
 *   alt="Hero background"
 * />
 *
 * // With eager loading and a custom class for styling
 * <HeroImage
 *   src="https://example.com/hero.jpg"
 *   alt="Hero image"
 *   loading="eager"
 *   className="custom-image"
 *   onLoad={() => console.log('Image loaded')}
 * />
 * ```
 *
 * @param props - The component props
 * @returns A memoized image element with optimizations
 */
const HeroImage = memo(
  ({
    src,
    alt,
    loading = 'lazy',
    className = '',
    decoding = 'async',
    onLoad,
    onError,
  }: HeroImageProps) => {
    const cssClasses = useMemo(() => {
      const baseClass = 'hero-image';
      return className ? `${baseClass} ${className}` : baseClass;
    }, [className]);

    return (
      <img
        src={src}
        alt={alt}
        loading={loading}
        decoding={decoding}
        className={cssClasses}
        onLoad={onLoad}
        onError={onError}
        // Accessibility and performance attributes
        draggable={false}
        // Prevent context menu on right-click for better UX
        onContextMenu={(e) => e.preventDefault()}
        // Ensure proper semantic meaning
        role="img"
      />
    );
  }
);

// Set display name for better debugging experience
HeroImage.displayName = 'HeroImage';

export default HeroImage;
